import {isString} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PredicateOf, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AnalyticsMetricsKeys} from '../../../../models/api/analyticsMetricsModel';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {AnalyticsEducationTooltip, AnalyticsTooltip} from '../tooltips/analyticsTooltips';
import {ReportSectionFrame} from './reportSectionFrame';
import {ReportSectionMetricSelectorDropdown} from './reportSectionMetricSelectionDropdown';

/*
 * Styles.
 */

const StyledReportSectionWrapperDiv = styled(ReportSectionFrame)<{
  $isEmbedded: boolean;
  $minHeight: number;
  $isExpanded: boolean;
}>`
  min-height: ${(p) => `${p.$isExpanded ? `${p.$minHeight}px` : 0}`};
  ${({$isEmbedded, $isExpanded, theme}) =>
    !$isEmbedded
      ? css`
          padding: 11px 15px ${$isExpanded ? '15px ' : '0'} 15px;
        `
      : css`
          height: 100%;
          border: inherit;
        `}
`;

function addGSectionTitleGridStyles($isCollapsible?: boolean) {
  return css`
    display: grid;
    grid-template-columns: ${$isCollapsible ? 'auto' : ''} auto 16px;
    align-items: center;
    gap: 4px;
  `;
}

const StyledReportSectionHeaderDiv = styled.div`
  ${flexCenter};
  gap: 8px;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-right: -4px;
`;

const StyledRightSectionDiv = styled.div`
  display: grid;
  align-items: center;
  justify-content: flex-end;
  // 1 column for each right section element (metric selector, custom content, export button, settings button) - update if needed.
  grid-template-columns: repeat(4, auto);

  > *:not(:first-child) {
    margin-left: 8px;
  }
`;

const StyledChromelessButton = styled(ChromelessButton)<{$isExpanded: boolean}>`
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom-left-radius: ${(p) => (p.$isExpanded ? 0 : 12)}px;
  border-bottom-right-radius: ${(p) => (p.$isExpanded ? 0 : 12)}px;
  // Making the button fill up the space.
  margin: -11px -15px 0px -15px;
  padding: 11px 15px 0px 15px;
  &:hover {
    background: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledTitleWrapperDiv = styled.div<{$isCollapsible: boolean}>`
  color: ${(p) => p.theme.greys.shade90};
  ${(p) => addGSectionTitleGridStyles(p.$isCollapsible)}
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  white-space: nowrap;
  line-height: 26px; // Making sure the title takes the same height as the metric selector dropdown.
`;

/*
 * Props.
 */

interface ReportSectionProps {
  title: ReactNode;
  reportSectionId: ReportElementIdsEnum; // Used for tracking purpose and to save the list of selected metrics for each report element.

  collapseItemKey?: string; // If provided, will make the section collapsible with this key as the unique identifier.
  hasMetricMultiselect?: boolean;
  isEmbedded?: boolean;
  selectedMetricIds?: Array<AnalyticsMetricsKeys>;
  isDisabled: boolean;
  sectionTooltip?: AnalyticsEducationTooltip;
  customContentRenderer?: (isDisabled: boolean) => ReactNode;
  minHeight?: number;
  settingsDropdownRenderer?: RendererOf<PopoverTransferProps>;
  setShouldShowExportDialog?: (val: boolean) => void;
  metricSelectionDropdownPredicate?: PredicateOf<AnalyticsMetricsKeys>;
  onMetricSelection?: (selectedMetricIds: ReadonlyArray<AnalyticsMetricsKeys>) => void;
  isTable?: boolean;

  onMouseOver?: () => void;
  onMouseLeave?: () => void;

  className?: string;
  // If false, we won't fetch hidden metrics when selecting a new metric in the dropdown. This is typically used in tables where submetrics are already computed.
  shouldFetchHiddenMetrics?: boolean;
}

export const ReportSection: FC<PropsWithChildren<ReportSectionProps>> = ({
  reportSectionId,
  title,
  hasMetricMultiselect,
  sectionTooltip,
  selectedMetricIds,
  isDisabled,
  settingsDropdownRenderer,
  customContentRenderer,
  setShouldShowExportDialog,
  metricSelectionDropdownPredicate,
  onMetricSelection,
  isEmbedded = false,
  collapseItemKey,
  minHeight = 0,
  className,
  children,
  onMouseOver,
  onMouseLeave,
  isTable,
  shouldFetchHiddenMetrics = true,
}) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const findIsExpanded = useAppSelector(getFindIsExpanded);

  const [displayTooltipIcon, setDisplayTooltipIcon] = useState(false);

  const onMouseOverHandler = () => {
    if (sectionTooltip) {
      setDisplayTooltipIcon(true);
    }
    onMouseOver?.();
  };

  const onMouseLeaveHandler = () => {
    if (sectionTooltip) {
      setDisplayTooltipIcon(false);
    }
    onMouseLeave?.();
  };

  // Collapse logic.
  const isCollapsible = isString(collapseItemKey);
  const isExpanded = !isCollapsible || findIsExpanded(collapseItemKey, true);
  const toggleIsExpanded = () => collapseItemKey && dispatch(toggleExpandItemAction(collapseItemKey, true));
  const maybeWrapWithCollapseButton = (content: ReactNode) => {
    if (!isCollapsible) {
      return content;
    }

    return (
      <StyledChromelessButton $isExpanded={isExpanded} onClick={() => toggleIsExpanded()}>
        {content}
      </StyledChromelessButton>
    );
  };

  return (
    <StyledReportSectionWrapperDiv
      className={className}
      $isEmbedded={isEmbedded}
      $isExpanded={isExpanded}
      $minHeight={minHeight}
      onFocus={onMouseOverHandler}
      onMouseOver={onMouseOverHandler}
      onMouseLeave={onMouseLeaveHandler}
    >
      {maybeWrapWithCollapseButton(
        <StyledReportSectionHeaderDiv>
          <StyledTitleWrapperDiv $isCollapsible={isCollapsible}>
            {isCollapsible && (
              <IconButton
                iconName={isExpanded ? 'chevronDownSmall' : 'chevronRightSmall'}
                label={
                  isExpanded ? (
                    <Trans id="analytics-report-section-collapse">Collapse</Trans>
                  ) : (
                    <Trans id="analytics-report-section-expand">Expand</Trans>
                  )
                }
                backgroundHoverColor={theme.greys.shade10}
              />
            )}
            {title}
            {maybeRenderTooltipIcon(displayTooltipIcon, reportSectionId, sectionTooltip)}
          </StyledTitleWrapperDiv>
          <StyledRightSectionDiv>
            <ReportSectionMetricSelectorDropdown
              isDisabled={isDisabled}
              reportSectionId={reportSectionId}
              hasMetricMultiselect={hasMetricMultiselect}
              selectedMetricIds={selectedMetricIds}
              metricSelectionDropdownPredicate={metricSelectionDropdownPredicate}
              onMetricSelection={onMetricSelection}
              isTable={isTable}
              shouldFetchHiddenMetrics={shouldFetchHiddenMetrics}
            />
            {customContentRenderer?.(isDisabled)}
            {maybeRenderExportButton(theme, setShouldShowExportDialog, isDisabled)}
            {maybeRenderSettingsButton(theme, settingsDropdownRenderer, isDisabled)}
          </StyledRightSectionDiv>
        </StyledReportSectionHeaderDiv>,
      )}
      {isExpanded && children}
    </StyledReportSectionWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderTooltipIcon(
  displayTooltipIcon: boolean,
  reportSectionId: ReportElementIdsEnum,
  sectionTooltip: AnalyticsEducationTooltip | undefined,
) {
  if (!sectionTooltip || !displayTooltipIcon) {
    return null;
  }

  return <AnalyticsTooltip tooltip={sectionTooltip} reportSectionId={reportSectionId} />;
}

function maybeRenderExportButton(
  theme: UserTheme,
  setShouldShowExportDialog: ((val: boolean) => void) | undefined,
  isDisabled: boolean,
) {
  if (!setShouldShowExportDialog) {
    return null;
  }

  return (
    <IconButton
      isDisabled={isDisabled}
      iconName="exportSmall"
      iconSize={16}
      label={<Trans id="analytics-export-button">Export</Trans>}
      color={theme.greys.shade80}
      onClick={() => setShouldShowExportDialog(true)}
    />
  );
}

function maybeRenderSettingsButton(
  theme: UserTheme,
  settingsDropdownRenderer: RendererOf<PopoverTransferProps> | undefined,
  isDisabled: boolean,
) {
  if (!settingsDropdownRenderer) {
    return null;
  }

  return (
    <PopoverCoordinator
      isDisabled={isDisabled}
      renderButton={() => (
        <IconButton
          iconName="moreVerticalSmall"
          iconSize={16}
          color={theme.greys.shade80}
          label={<Trans id="analytics-settings-button">Settings</Trans>}
        />
      )}
      renderModal={settingsDropdownRenderer}
    />
  );
}
